import { useEffect, useRef, useState } from 'react';
import { states } from '../../../../../utils/states';
// API
import { toast } from 'react-toastify';
import { useEnv } from '../../../../../context/env-context';
import { useAuth } from '../../../../../hooks/useAuth';
import { makeRequest } from '../../../../../utils/makeRequest';
import { useBasisTheory } from '@basis-theory/react-elements';
// components
import Button from 'react-bootstrap/Button';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { Spinner } from '../../../../../components/Spinner';
import CardProviderImage from '../../../../../components/CardProviderImage';
import ConsumerCardInputForm from './ConsumerCardInputForm';
import ConsumerBillingAddressForm from './ConsumerBillingAddressForm';
// constants
import { CARD_LINK_RESPONSE_MESSAGES } from '../../../../../constants/consumers';

function ConsumerPopoverNewCardForm({
  buttonTitle = 'Link Card',
  getAstraUser,
  getCards,
  cardsBlockStatus,
  getCardsBlockStatus,
  consumerId,
  consumerFirstName,
  consumerLastName,
  consumerAddressLine1,
  consumerAddressLine2,
  consumerCity,
  consumerState,
  consumerZip,
}) {
  const { envName, apiOriginConsumer } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [spinner, setSpinner] = useState(false);

  const btEnvironment = envName === 'prod' ? 'us' : 'test';
  const { bt } = useBasisTheory(process.env.REACT_APP_BT_PUBLIC_KEY, {
    elements: true,
    environment: btEnvironment,
  });

  const cardNumberRef = useRef(null);
  const cardExpirationDateRef = useRef(null);
  const cardVerificationCodeRef = useRef(null);

  const [btElementsEvents, setBtElementsEvents] = useState({
    cardExpirationDate: { ready: false, focused: false, dirty: false, changeEvent: null },
    cvc: { ready: false, focused: false, dirty: false, changeEvent: null },
    cardNumber: { ready: false, focused: false, dirty: false, changeEvent: null },
  });

  const [btMetadata, setBtMetadata] = useState({
    firstName: { value: '', dirty: false },
    lastName: { value: '', dirty: false },
    addressLine1: { value: '', dirty: false },
    addressLine2: { value: '', dirty: false },
    city: { value: '', dirty: false },
    state: { value: '', dirty: false },
    zip: { value: '', dirty: false },
  });

  const [useProfileAddress, setUseProfileAddress] = useState(true);

  useEffect(() => {
    if (useProfileAddress) {
      setBtMetadata(prevM => ({
        ...prevM,
        addressLine1: { value: consumerAddressLine1, dirty: false },
        addressLine2: { value: consumerAddressLine2, dirty: false },
        city: { value: consumerCity, dirty: false },
        state: { value: consumerState, dirty: false },
        zip: { value: consumerZip, dirty: false },
      }));
    } else {
      setBtMetadata(prevM => ({
        ...prevM,
        addressLine1: { value: '', dirty: false },
        addressLine2: { value: '', dirty: false },
        city: { value: '', dirty: false },
        state: { value: '', dirty: false },
        zip: { value: '', dirty: false },
      }));
    }
  }, [
    consumerAddressLine1,
    consumerAddressLine2,
    consumerCity,
    consumerState,
    consumerZip,
    useProfileAddress,
  ]);

  const handleToggle = () => {
    cardNumberRef.current?.clear();
    cardExpirationDateRef.current?.clear();
    cardVerificationCodeRef.current?.clear();

    setBtElementsEvents({
      cardExpirationDate: { ready: false, focused: false, dirty: false, changeEvent: null },
      cvc: { ready: false, focused: false, dirty: false, changeEvent: null },
      cardNumber: { ready: false, focused: false, dirty: false, changeEvent: null },
    });

    setBtMetadata({
      firstName: { value: consumerFirstName, dirty: false },
      lastName: { value: consumerLastName, dirty: false },
      addressLine1: { value: consumerAddressLine1, dirty: false },
      addressLine2: { value: consumerAddressLine2, dirty: false },
      city: { value: consumerCity, dirty: false },
      state: { value: consumerState, dirty: false },
      zip: { value: consumerZip, dirty: false },
    });

    setUseProfileAddress(true);
    setIsOpen(!isOpen);
  };

  const createBtToken = async () => {
    const handleCVC = () => {
      if (
        (envName === 'dev' || envName === 'stage') &&
        (btElementsEvents.cvc.changeEvent === null || btElementsEvents.cvc.changeEvent?.empty) &&
        btElementsEvents.cardNumber.changeEvent?.cardBrand === 'korean'
      ) {
        return {};
      }

      return { cvc: cardVerificationCodeRef.current };
    };

    try {
      const btToken = await bt?.tokens.create({
        type: 'card',
        data: {
          number: cardNumberRef.current,
          expiration_month: cardExpirationDateRef.current.month(),
          expiration_year: cardExpirationDateRef.current.year(),
          ...handleCVC(),
        },
        metadata: {
          firstName: btMetadata.firstName.value,
          lastName: btMetadata.lastName.value,
          addressLine1: btMetadata.addressLine1.value || undefined,
          addressLine2: btMetadata.addressLine2.value || undefined,
          city: btMetadata.city.value || undefined,
          state: btMetadata.state.value || undefined,
          zip: btMetadata.zip.value || undefined,
        },
      });

      return btToken;
    } catch (error) {
      console.error(error);

      toast.error('Unknown Error');

      return null;
    }
  };

  const linkCard = async () => {
    setSpinner(true);

    const token = await getAccessTokenSilently();

    const btToken = await createBtToken();

    if (!token || !btToken) {
      setSpinner(false);

      return;
    }

    try {
      const config = {
        url: `${apiOriginConsumer}/cards/link`,
        token,
        method: 'POST',
        data: JSON.stringify({
          btTokenId: btToken.id,
          isAdhoc: false,
          allowPending: true,
          consumerId,
        }),
      };

      const response = await makeRequest(config);

      await getAstraUser();
      await getCards();

      if (CARD_LINK_RESPONSE_MESSAGES[response.verificationResponse]) {
        toast.success(CARD_LINK_RESPONSE_MESSAGES[response.verificationResponse]);
      }

      handleToggle();
    } catch (error) {
      const response = await getCardsBlockStatus();

      if (response.blocked) {
        handleToggle();
      }

      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  };

  const validateCardNumber = () =>
    btElementsEvents.cardNumber.ready && btElementsEvents.cardNumber.changeEvent?.valid;

  const validateCardExpirationDate = () =>
    btElementsEvents.cardExpirationDate.ready &&
    btElementsEvents.cardExpirationDate.changeEvent?.valid;

  const validateCardVerificationCode = () => {
    if (!btElementsEvents.cardExpirationDate.ready) return false;

    if (
      (envName === 'dev' || envName === 'stage') &&
      (btElementsEvents.cvc.changeEvent === null || btElementsEvents.cvc.changeEvent?.empty) &&
      btElementsEvents.cardNumber.changeEvent?.cardBrand === 'korean'
    ) {
      return true;
    }

    return btElementsEvents.cvc.changeEvent?.valid;
  };

  const validateMetadata = () => {
    if (btMetadata.firstName.value.length === 0 || btMetadata.lastName.value.length === 0)
      return false;

    if (
      (envName === 'dev' || envName === 'stage') &&
      btElementsEvents.cardNumber.changeEvent?.cardBrand === 'korean'
    ) {
      return true;
    }

    return (
      btMetadata.addressLine1.value.length !== 0 &&
      btMetadata.city.value.length !== 0 &&
      states.includes(btMetadata.state.value) &&
      btMetadata.zip.value.length === 5
    );
  };

  const formValid =
    validateCardNumber() &&
    validateCardExpirationDate() &&
    validateCardVerificationCode() &&
    validateMetadata();

  return (
    <OverlayTrigger
      show={isOpen}
      placement="bottom-end"
      popperConfig={{ modifiers: [{ name: 'offset', options: { offset: [0, 6] } }] }}
      overlay={
        <Popover className="custom-popover">
          <Popover.Body className="bo-common-popover-form-menu bo-w-300">
            <Spinner
              visible={
                spinner ||
                !btElementsEvents.cardNumber.ready ||
                !btElementsEvents.cardExpirationDate.ready ||
                !btElementsEvents.cvc.ready
              }
              size={36}
            >
              <div className="mt-2 d-flex align-items-center justify-content-center">
                <CardProviderImage company="visa" height={14} className="me-3" />
                <CardProviderImage company="mastercard" height={20} />
              </div>
              <ConsumerCardInputForm
                bt={bt}
                btElementsEvents={btElementsEvents}
                btMetadata={btMetadata}
                setBtElementsEvents={setBtElementsEvents}
                setBtMetadata={setBtMetadata}
                cardNumberRef={cardNumberRef}
                cardExpirationDateRef={cardExpirationDateRef}
                cardVerificationCodeRef={cardVerificationCodeRef}
              />
              <ConsumerBillingAddressForm
                btElementsEvents={btElementsEvents}
                btMetadata={btMetadata}
                setBtMetadata={setBtMetadata}
                useProfileAddress={useProfileAddress}
                setUseProfileAddress={setUseProfileAddress}
              />
              <div className="mt-2 d-flex justify-content-between">
                <Button className="bo-common-button" onClick={linkCard} disabled={!formValid}>
                  Link
                </Button>
                <Button className="bo-common-button" onClick={handleToggle}>
                  Cancel
                </Button>
              </div>
            </Spinner>
          </Popover.Body>
        </Popover>
      }
    >
      <Button
        className="bo-common-button px-2"
        onClick={handleToggle}
        disabled={cardsBlockStatus.blocked}
      >
        <p className="bo-common-text-sm">{buttonTitle}</p>
      </Button>
    </OverlayTrigger>
  );
}

export default ConsumerPopoverNewCardForm;
